import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { AIGenerator } from "../components/AIGenerator";
import { ImageStudio } from "../components/ImageStudio";
import { useConfirm } from "../components/useConfirm";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { Plus, Pencil, Trash2, Copy, Check, Clock, UserPlus, Users, Headphones, Send, HeartPulse, ArrowRight, Lock, Settings as SettingsIcon } from "lucide-react";

const STEPS = ["Primo contatto", "Presentazione", "Formazione base", "Attivazione", "Avvio attività", "Autonomo"];
const MILESTONES = [3, 7, 14, 21];
const today = () => new Date().toISOString().slice(0, 10);
const addDays = (iso, n) => { const d = new Date(iso); d.setDate(d.getDate() + n); return d; };
const dLabel = (d) => d.toLocaleDateString("it-IT", { day: "numeric", month: "short" });

export function Wellness({ onNavigate }) {
  const [tab, setTab] = useState("recruit");
  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="wellness-view">
      <div className="inline-flex gap-1 p-1 rounded-xl bg-white/5 border border-white/10">
        <TabBtn id="recruit" tab={tab} setTab={setTab} icon={UserPlus} label="Reclutamento & Formazione" />
        <TabBtn id="clients" tab={tab} setTab={setTab} icon={Headphones} label="Clienti & Assistenza" />
      </div>
      {tab === "recruit" ? <RecruitPanel /> : <ClientsPanel onNavigate={onNavigate} />}
    </div>
  );
}

function WellnessImages() {
  const [prompt, setPrompt] = useState("");
  return <ImageStudio prompt={prompt} setPrompt={setPrompt} sector="wellness" />;
}

function TabBtn({ id, tab, setTab, icon: Icon, label }) {
  const on = tab === id;
  return (
    <button data-testid={`wellness-tab-${id}`} onClick={() => setTab(id)}
      className="flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg text-sm font-medium transition-colors"
      style={on ? { background: "rgba(16,185,129,0.15)", color: "#10B981" } : { color: "#94a3b8" }}>
      <Icon className="h-4 w-4" /> <span className="hidden sm:inline">{label}</span>
    </button>
  );
}

/* ---------------- Reclutamento ---------------- */
function RecruitPanel() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", step: STEPS[0], start_date: today(), notes: "" });
  const { confirm, ConfirmUI } = useConfirm();

  const load = () => api.get("/candidates").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ name: "", step: STEPS[0], start_date: today(), notes: "" }); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...c }); setOpen(true); };
  const save = async () => {
    if (!form.name.trim()) { toast.error("Inserisci il nome"); return; }
    if (editing) await api.put(`/candidates/${editing.id}`, form); else await api.post("/candidates", form);
    toast.success("Salvato"); setOpen(false); load();
  };
  const remove = async (c) => {
    if (!(await confirm({ title: "Eliminare il candidato?", description: `Rimuovere ${c.name}?`, confirmLabel: "Elimina", variant: "danger" }))) return;
    await api.delete(`/candidates/${c.id}`); toast.success("Eliminato"); load();
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
      <div className="glass rounded-2xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2"><Users className="h-5 w-5 text-emerald-400" /><h3 className="font-display font-bold">Candidati & Onboarding</h3></div>
          <button data-testid="new-candidate-button" onClick={openNew} className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-xl bg-emerald-500 text-white font-semibold hover:bg-emerald-400 transition-colors">
            <Plus className="h-4 w-4" /> Nuovo
          </button>
        </div>
        <div className="space-y-2">
          {list.length === 0 && <p className="text-sm text-slate-500">Nessun candidato. Aggiungi il primo collaboratore in onboarding.</p>}
          {list.map((c) => {
            const idx = STEPS.indexOf(c.step);
            return (
              <div key={c.id} className="p-3 rounded-xl bg-white/[0.03] border border-white/5 group" data-testid={`candidate-${c.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-semibold">{c.name}</p>
                    <p className="text-xs text-slate-500">Dal {new Date(c.start_date).toLocaleDateString("it-IT")}{c.notes ? ` · ${c.notes}` : ""}</p>
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button data-testid={`edit-candidate-${c.id}`} onClick={() => openEdit(c)} className="p-1.5 rounded-lg hover:bg-white/10"><Pencil className="h-3.5 w-3.5 text-slate-400" /></button>
                    <button data-testid={`delete-candidate-${c.id}`} onClick={() => remove(c)} className="p-1.5 rounded-lg hover:bg-rose-500/20"><Trash2 className="h-3.5 w-3.5 text-rose-400" /></button>
                  </div>
                </div>
                <div className="flex gap-1 mt-2">
                  {STEPS.map((s, si) => (
                    <div key={s} title={s} className="h-1.5 flex-1 rounded-full" style={{ background: si <= idx ? "#10B981" : "rgba(255,255,255,0.08)" }} />
                  ))}
                </div>
                <p className="text-[11px] text-emerald-300 mt-1.5">{c.step}</p>
              </div>
            );
          })}
        </div>
      </div>

      <AIGenerator kind="wellness_freedom" sector="wellness"
        title="Idee: libertà & crescita"
        subtitle="Contenuti su libertà lavorativa, crescita personale e lavoro indipendente"
        placeholder="Es. parla alle mamme che vogliono indipendenza…" />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="glass border-white/10 max-w-md" data-testid="candidate-modal">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Modifica candidato" : "Nuovo candidato"}</DialogTitle>
            <DialogDescription className="text-slate-400">Traccia lo step formativo del collaboratore.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <input data-testid="candidate-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nome e cognome" className="w-full bg-white/5 border border-white/10 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none" />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-400">Step formativo</label>
                <select data-testid="candidate-step-select" value={form.step} onChange={(e) => setForm({ ...form, step: e.target.value })} className="w-full mt-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm focus:outline-none">
                  {STEPS.map((s) => <option key={s} value={s} className="bg-slate-900">{s}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-400">Data inizio</label>
                <input data-testid="candidate-date-input" type="date" value={form.start_date?.slice(0, 10)} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="w-full mt-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm focus:outline-none" />
              </div>
            </div>
            <textarea data-testid="candidate-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Note" rows={2} className="w-full bg-white/5 border border-white/10 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none resize-none" />
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm hover:bg-white/10">Annulla</button>
            <button data-testid="save-candidate-button" onClick={save} className="px-4 py-2.5 rounded-xl bg-emerald-500 text-white text-sm font-semibold hover:bg-emerald-400">Salva</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {ConfirmUI}
    </div>
  );
}

/* ---------------- Flusso a due livelli ---------------- */
function FlowExplainer() {
  return (
    <div className="glass rounded-2xl p-5" data-testid="flow-explainer">
      <h3 className="font-display font-bold mb-3">Il flusso a due livelli</h3>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-4 items-stretch">
        <div className="rounded-xl p-4 border" style={{ background: "rgba(34,158,217,0.08)", borderColor: "rgba(34,158,217,0.25)" }}>
          <div className="flex items-center gap-2 mb-2"><Send className="h-4 w-4" style={{ color: "#229ED9" }} /><p className="font-semibold text-sm" style={{ color: "#5bc0f0" }}>Livello 1 · Telegram</p></div>
          <p className="text-xs text-slate-400 mb-2">Immediato e senza barriere: accogli clienti e collaboratori.</p>
          <ul className="text-xs text-slate-300 space-y-1">
            <li>• <strong>Canali</strong> → contenuti, ricette e formazione (broadcast)</li>
            <li>• <strong>Gruppi</strong> → community, supporto e domande</li>
          </ul>
        </div>
        <div className="hidden md:flex items-center justify-center"><ArrowRight className="h-6 w-6 text-slate-600" /></div>
        <div className="rounded-xl p-4 border" style={{ background: "rgba(16,185,129,0.08)", borderColor: "rgba(16,185,129,0.25)" }}>
          <div className="flex items-center gap-2 mb-2"><HeartPulse className="h-4 w-4 text-emerald-400" /><p className="font-semibold text-sm text-emerald-300">Livello 2 · HgoWeb</p></div>
          <p className="text-xs text-slate-400 mb-2">Esclusivo e avanzato: solo su autorizzazione manuale.</p>
          <ul className="text-xs text-slate-300 space-y-1">
            <li>• Invii il <strong>test di Autovalutazione del Benessere</strong></li>
            <li>• <Lock className="inline h-3 w-3" /> Sblocchi manualmente l'<strong>area corsi avanzata</strong> a chi lo completa</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Messaggi pronti (auto-compilati da "I Miei Link") ---------------- */
const TEMPLATES = [
  {
    id: "ricette", title: "Invito · Canale Benessere & Ricette", linkKey: "link_canale_ricette",
    build: (n, l) => `Ciao! 🌿 Sono ${n}. Ogni settimana condivido ricette proteiche, idee sane e consigli di benessere nel mio Canale Telegram Benessere & Ricette. Entra gratis qui 👉 ${l}`,
  },
  {
    id: "supporto", title: "Invito · Gruppo Community & Supporto", linkKey: "link_gruppo_supporto",
    build: (n, l) => `Ciao! 😊 Sono ${n}. Ti aggiungo alla nostra community Telegram di benessere: è lo spazio dove fare domande, confrontarti e ricevere supporto ogni giorno. Unisciti qui 👉 ${l}`,
  },
  {
    id: "formazione", title: "Invito · Canale Formazione (nuovi incaricati)", linkKey: "link_canale_formazione",
    build: (n, l) => `Ciao! 🚀 Sono ${n}. Se vuoi capire come funziona questa attività e partire con il piede giusto, nel mio Canale Telegram di Formazione trovi tutti i materiali per i nuovi incaricati. Entra qui 👉 ${l}`,
  },
  {
    id: "team", title: "Invito · Gruppo Team Collaboratori", linkKey: "link_gruppo_team",
    build: (n, l) => `Benvenuto/a nel team! 🤝 Sono ${n}. Questo è il nostro Gruppo Telegram operativo: ci coordiniamo, condividiamo risultati e ci diamo una mano a vicenda. Entra qui 👉 ${l}`,
  },
  {
    id: "autovalutazione", title: "Invito · Test Autovalutazione Benessere (HgoWeb)", linkKey: "link_autovalutazione",
    build: (n, l) => `Ciao! 🌱 Sono ${n}. Il primo passo del percorso è l'Autovalutazione del Benessere: un breve test gratuito che ci aiuta a capire da dove partire e a costruire il tuo percorso su misura. Compilalo qui 👉 ${l}`,
  },
  {
    id: "sblocco", title: "Sblocco · Autorizzazione area corsi (HgoWeb)", linkKey: null,
    build: (n) => `Perfetto! ✅ Ho ricevuto la tua Autovalutazione del Benessere e ti ho appena autorizzato/a manualmente all'accesso dell'area corsi avanzata su HgoWeb. Ora puoi entrare con le tue credenziali e iniziare il percorso formativo. Per qualsiasi dubbio scrivimi pure! Un caro saluto, ${n}`,
  },
];

function MessageTemplates({ onNavigate }) {
  const [profile, setProfile] = useState(null);
  useEffect(() => { api.get("/profile").then((r) => setProfile(r.data)); }, []);

  const name = (profile?.name || "").trim();
  const incomplete = !name;

  const copy = (t, disabled) => {
    if (disabled) { toast.error("Compila prima il link in Impostazioni → I Miei Link"); return; }
    navigator.clipboard.writeText(t); toast.success("Messaggio copiato!");
  };

  return (
    <div className="glass rounded-2xl p-5" data-testid="message-templates">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2"><Send className="h-5 w-5 text-emerald-400" /><h3 className="font-display font-bold">Messaggi pronti</h3></div>
        <button data-testid="open-settings-button" onClick={() => onNavigate && onNavigate("settings")}
          className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors">
          <SettingsIcon className="h-3.5 w-3.5" /> I Miei Link
        </button>
      </div>
      <p className="text-xs text-slate-400 mb-3">Si compilano con il tuo nome e i tuoi link salvati in <span className="text-emerald-300">Impostazioni</span>.</p>

      {incomplete && (
        <div className="mb-3 px-3 py-2.5 rounded-xl text-xs flex items-center justify-between gap-2" style={{ background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.25)" }}>
          <span className="text-amber-200">Imposta il tuo <strong>Nome</strong> e i <strong>link</strong> per completare i messaggi.</span>
          <button onClick={() => onNavigate && onNavigate("settings")} className="shrink-0 text-amber-300 underline">Vai a Impostazioni</button>
        </div>
      )}

      <div className="space-y-2.5">
        {TEMPLATES.map((tpl) => {
          const nm = name || "[il tuo nome]";
          const link = tpl.linkKey ? (profile?.[tpl.linkKey] || "").trim() : "";
          const missingLink = tpl.linkKey && !link;
          const text = tpl.linkKey ? tpl.build(nm, link || "[inserisci il link in Impostazioni]") : tpl.build(nm);
          const disabled = incomplete || missingLink;
          return (
            <div key={tpl.id} className="p-3 rounded-xl bg-white/[0.03] border border-white/5" data-testid={`template-${tpl.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-sm">{tpl.title}</p>
                  <p className="text-xs text-slate-400 mt-1 line-clamp-2">{text}</p>
                  {missingLink && <p className="text-[11px] text-amber-400 mt-1">Link mancante — aggiungilo in Impostazioni.</p>}
                </div>
                <button data-testid={`copy-template-${tpl.id}`} onClick={() => copy(text, disabled)}
                  className="shrink-0 flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg font-medium transition-colors"
                  style={disabled ? { background: "rgba(255,255,255,0.04)", color: "#64748b" } : { background: "rgba(16,185,129,0.15)", color: "#10B981" }}>
                  <Copy className="h-3.5 w-3.5" /> Copia
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ---------------- Clienti & Assistenza ---------------- */
function ClientsPanel({ onNavigate }) {
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", program: "", start_date: today(), notes: "", done_checkins: [] });
  const { confirm, ConfirmUI } = useConfirm();

  const load = () => api.get("/clients").then((r) => setClients(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ name: "", program: "", start_date: today(), notes: "", done_checkins: [] }); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...c }); setOpen(true); };
  const save = async () => {
    if (!form.name.trim()) { toast.error("Inserisci il nome"); return; }
    if (editing) await api.put(`/clients/${editing.id}`, form); else await api.post("/clients", form);
    toast.success("Salvato"); setOpen(false); load();
  };
  const removeClient = async (c) => {
    if (!(await confirm({ title: "Eliminare il cliente?", description: `Rimuovere ${c.name}?`, confirmLabel: "Elimina", variant: "danger" }))) return;
    await api.delete(`/clients/${c.id}`); toast.success("Eliminato"); load();
  };
  const toggleCheckin = async (c, m) => {
    const done = c.done_checkins || [];
    const next = done.includes(m) ? done.filter((x) => x !== m) : [...done, m];
    await api.put(`/clients/${c.id}`, { ...c, done_checkins: next });
    load();
  };

  return (
    <div className="space-y-6">
      <FlowExplainer />

      {/* Follow-up clienti */}
      <div className="glass rounded-2xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2"><Clock className="h-5 w-5 text-emerald-400" /><h3 className="font-display font-bold">Follow-up clienti (giorni 3 · 7 · 14 · 21)</h3></div>
          <button data-testid="new-client-button" onClick={openNew} className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-xl bg-emerald-500 text-white font-semibold hover:bg-emerald-400 transition-colors">
            <Plus className="h-4 w-4" /> Cliente
          </button>
        </div>
        <div className="space-y-3">
          {clients.length === 0 && <p className="text-sm text-slate-500">Nessun cliente. Aggiungi chi segui nel programma per ricevere i promemoria di check-in.</p>}
          {clients.map((c) => (
            <div key={c.id} className="p-3 rounded-xl bg-white/[0.03] border border-white/5 group" data-testid={`client-${c.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-semibold">{c.name}</p>
                  <p className="text-xs text-slate-500">Inizio {new Date(c.start_date).toLocaleDateString("it-IT")}{c.program ? ` · ${c.program}` : ""}</p>
                </div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button data-testid={`edit-client-${c.id}`} onClick={() => openEdit(c)} className="p-1.5 rounded-lg hover:bg-white/10"><Pencil className="h-3.5 w-3.5 text-slate-400" /></button>
                  <button data-testid={`delete-client-${c.id}`} onClick={() => removeClient(c)} className="p-1.5 rounded-lg hover:bg-rose-500/20"><Trash2 className="h-3.5 w-3.5 text-rose-400" /></button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {MILESTONES.map((m) => {
                  const due = addDays(c.start_date, m);
                  const done = (c.done_checkins || []).includes(m);
                  const overdue = !done && due <= new Date();
                  return (
                    <button key={m} data-testid={`checkin-${c.id}-${m}`} onClick={() => toggleCheckin(c, m)}
                      className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors"
                      style={done
                        ? { background: "rgba(16,185,129,0.18)", color: "#10B981", borderColor: "rgba(16,185,129,0.4)" }
                        : overdue
                          ? { background: "rgba(244,63,94,0.12)", color: "#fb7185", borderColor: "rgba(244,63,94,0.35)" }
                          : { color: "#94a3b8", borderColor: "rgba(255,255,255,0.08)" }}>
                      {done ? <Check className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
                      Giorno {m} · {dLabel(due)}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Messaggi pronti + AI */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <MessageTemplates onNavigate={onNavigate} />
        <AIGenerator kind="wellness_recipes" sector="wellness"
          title="Idee: ricette & benessere"
          subtitle="Ricette proteiche sane, abitudini di benessere e stile di vita attivo"
          placeholder="Es. colazione proteica veloce…" />
      </div>
      <WellnessImages />

      {/* Client modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="glass border-white/10 max-w-md" data-testid="client-modal">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Modifica cliente" : "Nuovo cliente"}</DialogTitle>
            <DialogDescription className="text-slate-400">La data d'inizio attiva i check-in a 3, 7, 14 e 21 giorni.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <input data-testid="client-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nome cliente" className="w-full bg-white/5 border border-white/10 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none" />
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="client-program-input" value={form.program} onChange={(e) => setForm({ ...form, program: e.target.value })} placeholder="Programma" className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm focus:outline-none" />
              <input data-testid="client-date-input" type="date" value={form.start_date?.slice(0, 10)} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm focus:outline-none" />
            </div>
            <textarea data-testid="client-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Note" rows={2} className="w-full bg-white/5 border border-white/10 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none resize-none" />
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm hover:bg-white/10">Annulla</button>
            <button data-testid="save-client-button" onClick={save} className="px-4 py-2.5 rounded-xl bg-emerald-500 text-white text-sm font-semibold hover:bg-emerald-400">Salva</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {ConfirmUI}
    </div>
  );
}
